import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  getProducts,
  createProduct,
  updateProduct,
  activateProduct,
  deactivateProduct,
  deleteProduct,
  uploadProductImage,
} from "../../Services/productService";
import { compressProductImage, getImageSrc } from "../../Services/mediaService";
import { getOrders, updateOrderStatus, getRecoveryRequests, approveRecoveryRequest } from "../../Services/adminService";
import {
  getContactMessages,
  updateContactMessageStatus,
} from "../../Services/contactService";
import "./AdminPanel.css";

export default function AdminPanel({ navigateTo }) {
  const {
    user,
    isAdmin,
    isStaff,
    usersList,
    fetchUsers,
    createStaff,
    updateUser,
    deleteUser,
    logout,
  } = useAuth();

  const [activeTab, setActiveTab] = useState("dashboard");
  const [searchTerm, setSearchTerm] = useState("");
  const [message, setMessage] = useState("");

  const [showStaffForm, setShowStaffForm] = useState(false);
  const [newStaff, setNewStaff] = useState({
    username: "",
    phone: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [formErrors, setFormErrors] = useState({});

  const [editingStaffId, setEditingStaffId] = useState(null);
  const [editStaff, setEditStaff] = useState({
    username: "",
    phone: "",
    email: "",
  });
  const [editStaffErrors, setEditStaffErrors] = useState({});

  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [productForm, setProductForm] = useState({
    title: "",
    img: "",
    price: "",
    count: "",
    category: "",
    description: "",
    isActive: true,
  });
  const [productErrors, setProductErrors] = useState({});
  const [productImageFile, setProductImageFile] = useState(null);
  const [productImageUploading, setProductImageUploading] = useState(false);

  const [contactMessages, setContactMessages] = useState([]);
  const [contactMessagesLoading, setContactMessagesLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [recoveryRequests, setRecoveryRequests] = useState([]);
  const [recoveryResetLink, setRecoveryResetLink] = useState(null);

  const hasAdminAccess = isAdmin || isStaff;
  const canManageProducts = isAdmin || isStaff;
  const canManageOrders = isAdmin || isStaff;

  useEffect(() => {
    if (!hasAdminAccess) {
      navigateTo("shop");
      return;
    }

    if (typeof fetchUsers === "function") {
      fetchUsers();
    }

    // محصولات و پیام‌ها عمداً اینجا load نمی‌شوند
    // تا صفحه وارد loop نشود. هر تب هنگام کلیک، اطلاعات خودش را می‌گیرد.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAdminAccess]);

  const showMessage = (text, duration = 3500) => {
    setMessage(text);

    setTimeout(() => {
      setMessage("");
    }, duration);
  };

  const formatPrice = (value) => {
    return Number(value || 0).toLocaleString("fa-IR");
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "-";

    try {
      return new Intl.DateTimeFormat("fa-IR-u-ca-persian", {
        year: "numeric",
        month: "long",
        day: "numeric",
      }).format(new Date(dateValue));
    } catch {
      return "-";
    }
  };

  const getRoleName = (role) => {
    switch (role) {
      case "super_admin":
        return "ادمین اصلی";
      case "staff":
        return "کارمند";
      case "customer":
        return "مشتری";
      default:
        return "نامشخص";
    }
  };

  const getRoleClass = (role) => {
    switch (role) {
      case "super_admin":
        return "role-super-admin";
      case "staff":
        return "role-staff";
      case "customer":
        return "role-customer";
      default:
        return "role-unknown";
    }
  };

  const getUserFullName = (item) => {
    const fullName = `${item?.firstName || ""} ${item?.lastName || ""}`.trim();

    return fullName || item?.username || item?.email || "-";
  };

  const getOrderCode = (order) => {
    return order?.orderCode || order?.id || "-";
  };

  const getOrderStatusLabel = (status) => {
    switch (status) {
      case "waiting_for_payment":
        return "در انتظار پرداخت";
      case "paid":
        return "پرداخت‌شده";
      case "processing":
        return "در حال آماده‌سازی";
      case "ready_to_ship":
        return "آماده ارسال";
      case "shipped":
        return "ارسال‌شده";
      case "delivered":
        return "تحویل‌شده";
      case "cancelled":
        return "لغوشده";
      default:
        return "ثبت‌شده";
    }
  };

  const getPaymentStatusLabel = (status) => {
    switch (status) {
      case "paid":
        return "پرداخت شد";
      case "failed":
        return "پرداخت رد شد";
      case "refunded":
        return "بازگشت وجه";
      case "pending":
      default:
        return "در انتظار پرداخت";
    }
  };

  const getPaymentMethodLabel = (method) => {
    switch (method) {
      case "online_gateway":
        return "درگاه آنلاین";
      case "card_to_card":
        return "کارت به کارت";
      case "cash_on_delivery":
        return "پرداخت هنگام تحویل";
      case "online_gateway_pending":
        return "درگاه آنلاین / غیرفعال";
      default:
        return "کارت به کارت / بررسی دستی";
    }
  };

  const getMessageStatusLabel = (status) => {
    switch (status) {
      case "new":
        return "جدید";
      case "seen":
        return "دیده‌شده";
      case "followed":
        return "پیگیری‌شده";
      case "closed":
        return "بسته‌شده";
      default:
        return "جدید";
    }
  };

  const customers = useMemo(() => {
    return usersList.filter((item) => item.role === "customer");
  }, [usersList]);

  const staffUsers = useMemo(() => {
    return usersList.filter((item) => item.role === "staff");
  }, [usersList]);

  const allOrders = useMemo(() => orders.map((order) => ({
    ...order,
    orderStatus: order.status || order.orderStatus,
    userName: `${order.customer?.firstName || ""} ${order.customer?.lastName || ""}`.trim() || order.customer?.name || "مهمان",
    userPhone: order.customer?.phone || "",
    userEmail: order.customer?.email || "",
    shippingMethod: order.shipping?.method,
    shippingTitle: order.shipping?.title,
  })), [orders]);

  const allPayments = useMemo(() => {
    return allOrders.map((order) => ({
      id: `payment-${order.userId}-${order.id}`,
      orderId: order.id,
      orderCode: getOrderCode(order),
      userId: order.userId,
      order,
      customerName:
        order.customer?.firstName || order.customer?.lastName
          ? `${order.customer?.firstName || ""} ${
              order.customer?.lastName || ""
            }`.trim()
          : order.userName,
      phone: order.customer?.phone || order.userPhone || "",
      amount: order.finalTotal || order.total || 0,
      paymentStatus: order.paymentStatus || "pending",
      paymentMethod: order.paymentMethod || "card_to_card",
      createdAt: order.paidAt || order.createdAt,
    }));
  }, [allOrders]);

  const stats = useMemo(() => {
    const paidOrders = allOrders.filter((order) => order.paymentStatus === "paid");
    const pendingPayments = allOrders.filter(
      (order) => order.paymentStatus !== "paid"
    );

    const totalSales = paidOrders.reduce((total, order) => {
      return total + Number(order.finalTotal || order.total || 0);
    }, 0);

    return {
      usersCount: usersList.length,
      customersCount: customers.length,
      staffCount: staffUsers.length,
      ordersCount: allOrders.length,
      paidOrdersCount: paidOrders.length,
      pendingPaymentsCount: pendingPayments.length,
      productsCount: products.length,
      activeProductsCount: products.filter((product) => product.isActive !== false)
        .length,
      messagesCount: contactMessages.length,
      newMessagesCount: contactMessages.filter((item) => item.status === "new").length,
      totalSales,
    };
  }, [usersList, customers, staffUsers, allOrders, products, contactMessages]);

  const getUsersForActiveTab = () => {
    if (activeTab === "customers") return customers;
    if (activeTab === "staff") return staffUsers;
    if (activeTab === "all-users") return usersList;

    return [];
  };

  const filteredUsers = getUsersForActiveTab().filter((userItem) => {
    const search = searchTerm.trim().toLowerCase();

    if (!search) return true;

    return (
      userItem.username?.toLowerCase().includes(search) ||
      userItem.firstName?.toLowerCase().includes(search) ||
      userItem.lastName?.toLowerCase().includes(search) ||
      userItem.phone?.includes(search) ||
      userItem.email?.toLowerCase().includes(search)
    );
  });

  const filteredOrders = allOrders.filter((order) => {
    const search = searchTerm.trim().toLowerCase();

    if (!search) return true;

    const orderItemsText = Array.isArray(order.items)
      ? order.items.map((item) => `${item.title} ${item.quantity}`).join(" ")
      : "";

    return (
      String(getOrderCode(order)).toLowerCase().includes(search) ||
      order.userName?.toLowerCase().includes(search) ||
      order.userPhone?.includes(search) ||
      order.userEmail?.toLowerCase().includes(search) ||
      orderItemsText.toLowerCase().includes(search)
    );
  });

  const filteredPayments = allPayments.filter((payment) => {
    const search = searchTerm.trim().toLowerCase();

    if (!search) return true;

    return (
      String(payment.orderCode || payment.orderId).toLowerCase().includes(search) ||
      payment.customerName?.toLowerCase().includes(search) ||
      payment.phone?.includes(search)
    );
  });

  const filteredProducts = products.filter((product) => {
    const search = searchTerm.trim().toLowerCase();

    if (!search) return true;

    return (
      product.title?.toLowerCase().includes(search) ||
      product.category?.toLowerCase().includes(search) ||
      product.description?.toLowerCase().includes(search) ||
      product.img?.toLowerCase().includes(search)
    );
  });

  const filteredContactMessages = contactMessages.filter((item) => {
    const search = searchTerm.trim().toLowerCase();

    if (!search) return true;

    return (
      item.name?.toLowerCase().includes(search) ||
      item.phone?.includes(search) ||
      item.email?.toLowerCase().includes(search) ||
      item.subject?.toLowerCase().includes(search) ||
      item.message?.toLowerCase().includes(search) ||
      getMessageStatusLabel(item.status).includes(search)
    );
  });

  const loadProducts = async () => {
    try {
      setProductsLoading(true);

      const result = await getProducts();

      setProducts(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error("Load products error:", error);
      showMessage("❌ خطا در دریافت محصولات");
    } finally {
      setProductsLoading(false);
    }
  };

  const loadContactMessages = async () => {
    try {
      setContactMessagesLoading(true);

      const result = await getContactMessages();

      setContactMessages(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error("Load contact messages error:", error);
      showMessage("❌ خطا در دریافت پیام‌ها");
    } finally {
      setContactMessagesLoading(false);
    }
  };

  const loadOrders = async () => {
    try { setOrders(await getOrders()); } catch (error) { console.error(error); showMessage("❌ خطا در دریافت سفارش‌ها"); }
  };

  const loadRecoveryRequests = async () => {
    try { setRecoveryRequests(await getRecoveryRequests()); } catch (error) { console.error(error); showMessage("❌ خطا در دریافت درخواست‌های بازیابی"); }
  };

  const handleChangeMessageStatus = async (messageId, status) => {
    try {
      await updateContactMessageStatus(messageId, status);
      showMessage("✅ وضعیت پیام به‌روزرسانی شد");
      await loadContactMessages();
    } catch (error) {
      console.error("Update message status error:", error);
      showMessage("❌ خطا در تغییر وضعیت پیام");
    }
  };

  const updateOrderForUser = async (order, updates, successMessage) => {
    if (!canManageOrders) {
      showMessage("⚠️ شما دسترسی مدیریت سفارش‌ها را ندارید");
      return;
    }

    try {
      await updateOrderStatus(order.id, { status: updates.orderStatus, paymentStatus: updates.paymentStatus });
      showMessage(successMessage || "✅ وضعیت سفارش به‌روزرسانی شد");
      await loadOrders();
    } catch (error) {
      console.error("Update order error:", error);
      showMessage("❌ خطا در تغییر وضعیت سفارش");
    }
  };

  const handleChangePaymentStatus = async (order, nextPaymentStatus) => {
    const updates = {
      paymentStatus: nextPaymentStatus,
      paymentMethod: "card_to_card",
    };

    if (
      nextPaymentStatus === "paid" &&
      (!order.orderStatus || order.orderStatus === "waiting_for_payment")
    ) {
      updates.orderStatus = "processing";
    }

    if (
      nextPaymentStatus === "failed" &&
      (!order.orderStatus || order.orderStatus === "waiting_for_payment")
    ) {
      updates.orderStatus = "waiting_for_payment";
    }

    await updateOrderForUser(
      order,
      updates,
      nextPaymentStatus === "paid"
        ? "✅ پرداخت سفارش تأیید شد"
        : "✅ وضعیت پرداخت سفارش تغییر کرد"
    );
  };

  const handleChangeOrderStatus = async (order, nextOrderStatus) => {
    const updates = {
      orderStatus: nextOrderStatus,
    };

    if (nextOrderStatus === "waiting_for_payment") {
      updates.paymentStatus = "pending";
      updates.paymentMethod = "card_to_card";
    }

    await updateOrderForUser(order, updates, "✅ وضعیت ارسال/سفارش تغییر کرد");
  };

  const handleQuickMarkPaid = async (order) => {
    await handleChangePaymentStatus(order, "paid");
  };

  const handleQuickMarkShipped = async (order) => {
    const updates = {
      orderStatus: "shipped",
    };

    if (order.paymentStatus !== "paid") {
      updates.paymentStatus = "paid";
      updates.paymentMethod = "card_to_card";
    }

    await updateOrderForUser(order, updates, "✅ سفارش به وضعیت ارسال‌شده تغییر کرد");
  };

  const handleStaffFormChange = (e) => {
    const { name, value } = e.target;

    setNewStaff((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateStaffForm = () => {
    const errors = {};

    if (!newStaff.username.trim()) {
      errors.username = "نام کاربری الزامی است";
    } else if (newStaff.username.length < 3) {
      errors.username = "نام کاربری حداقل باید ۳ کاراکتر باشد";
    }

    if (!newStaff.phone) {
      errors.phone = "شماره موبایل الزامی است";
    } else if (!/^[0-9]{11}$/.test(newStaff.phone)) {
      errors.phone = "شماره موبایل باید ۱۱ رقم باشد";
    }

    if (!newStaff.email) {
      errors.email = "ایمیل الزامی است";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newStaff.email)) {
      errors.email = "ایمیل معتبر نیست";
    }

    if (!newStaff.password) {
      errors.password = "رمز عبور الزامی است";
    } else if (newStaff.password.length < 8) {
      errors.password = "رمز عبور حداقل باید ۸ کاراکتر باشد";
    }

    if (newStaff.password !== newStaff.confirmPassword) {
      errors.confirmPassword = "رمز عبور با تکرار آن مطابقت ندارد";
    }

    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const resetStaffForm = () => {
    setNewStaff({
      username: "",
      phone: "",
      email: "",
      password: "",
      confirmPassword: "",
    });
    setFormErrors({});
    setShowStaffForm(false);
  };

  const handleCreateStaff = async () => {
    if (!isAdmin) {
      showMessage("⚠️ فقط ادمین اصلی می‌تواند کارمند ایجاد کند");
      return;
    }

    if (!validateStaffForm()) return;

    const result = await createStaff(
      newStaff.username,
      newStaff.phone,
      newStaff.email,
      newStaff.password
    );

    if (result.success) {
      showMessage("✅ کارمند جدید با موفقیت اضافه شد");
      resetStaffForm();

      if (typeof fetchUsers === "function") {
        await fetchUsers();
      }
    } else {
      setFormErrors({
        general: result.message || "خطا در ایجاد کارمند",
      });
    }
  };

  const openEditStaffForm = (staff) => {
    if (!isAdmin) {
      showMessage("⚠️ فقط ادمین اصلی می‌تواند اطلاعات کارمند را ویرایش کند");
      return;
    }

    setEditingStaffId(staff.id);
    setEditStaff({
      username: staff.username || "",
      phone: staff.phone || "",
      email: staff.email || "",
    });
    setEditStaffErrors({});
    setShowStaffForm(false);
  };

  const handleEditStaffChange = (e) => {
    const { name, value } = e.target;

    setEditStaff((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (editStaffErrors[name]) {
      setEditStaffErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateEditStaffForm = () => {
    const errors = {};

    if (!editStaff.username.trim()) {
      errors.username = "نام کاربری الزامی است";
    } else if (editStaff.username.length < 3) {
      errors.username = "نام کاربری حداقل باید ۳ کاراکتر باشد";
    }

    if (!editStaff.phone) {
      errors.phone = "شماره موبایل الزامی است";
    } else if (!/^[0-9]{11}$/.test(editStaff.phone)) {
      errors.phone = "شماره موبایل باید ۱۱ رقم باشد";
    }

    if (!editStaff.email) {
      errors.email = "ایمیل الزامی است";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editStaff.email)) {
      errors.email = "ایمیل معتبر نیست";
    }

    setEditStaffErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const resetEditStaffForm = () => {
    setEditingStaffId(null);
    setEditStaff({
      username: "",
      phone: "",
      email: "",
    });
    setEditStaffErrors({});
  };

  const handleUpdateStaff = async () => {
    if (!isAdmin) {
      showMessage("⚠️ فقط ادمین اصلی می‌تواند اطلاعات کارمند را ویرایش کند");
      return;
    }

    if (!editingStaffId) return;

    if (!validateEditStaffForm()) return;

    if (typeof updateUser !== "function") {
      showMessage("❌ تابع updateUser در AuthContext پیدا نشد");
      return;
    }

    const result = await updateUser(editingStaffId, {
      username: editStaff.username.trim(),
      phone: editStaff.phone.trim(),
      email: editStaff.email.trim(),
      updatedAt: new Date().toISOString(),
    });

    if (result.success) {
      showMessage("✅ اطلاعات کارمند با موفقیت ویرایش شد");
      resetEditStaffForm();

      if (typeof fetchUsers === "function") {
        await fetchUsers();
      }
    } else {
      setEditStaffErrors({
        general: result.message || "خطا در ویرایش اطلاعات کارمند",
      });
    }
  };

  const handleDeleteUser = async (userId, userRole) => {
    if (!isAdmin) {
      showMessage("⚠️ فقط ادمین اصلی می‌تواند کاربران را حذف کند");
      return;
    }

    if (userRole === "super_admin") {
      showMessage("⚠️ امکان حذف ادمین اصلی وجود ندارد");
      return;
    }

    if (String(userId) === String(user?.id)) {
      showMessage("⚠️ نمی‌توانید حساب خودتان را حذف کنید");
      return;
    }

    const confirmed = window.confirm("آیا از حذف این کاربر مطمئن هستید؟");

    if (!confirmed) return;

    const result = await deleteUser(userId);

    if (result.success) {
      showMessage("✅ کاربر با موفقیت حذف شد");

      if (typeof fetchUsers === "function") {
        await fetchUsers();
      }
    } else {
      showMessage("❌ " + (result.message || "خطا در حذف کاربر"));
    }
  };

  const resetProductForm = () => {
    setProductImageFile(null);
    setProductForm({
      title: "",
      img: "",
      price: "",
      count: "",
      category: "",
      description: "",
      isActive: true,
    });
    setProductErrors({});
    setEditingProductId(null);
    setShowProductForm(false);
  };

  const openNewProductForm = () => {
    if (!canManageProducts) {
      showMessage("⚠️ شما دسترسی مدیریت محصولات ندارید");
      return;
    }

    resetEditStaffForm();
    setProductForm({
      title: "",
      img: "",
      price: "",
      count: "",
      category: "سفال",
      description: "",
      isActive: true,
    });
    setProductErrors({});
    setProductImageFile(null);
    setEditingProductId(null);
    setShowProductForm(true);
  };

  const openEditProductForm = (product) => {
    if (!canManageProducts) {
      showMessage("⚠️ شما دسترسی ویرایش محصول ندارید");
      return;
    }

    setProductForm({
      title: product.title || "",
      img: product.img || "",
      price: product.price || "",
      count: product.count || "",
      category: product.category || "",
      description: product.description || "",
      isActive: product.isActive !== false,
    });
    setProductErrors({});
    setProductImageFile(null);
    setEditingProductId(product.id);
    setShowProductForm(true);
  };

  const handleProductFormChange = (e) => {
    const { name, value, type, checked } = e.target;

    setProductForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (productErrors[name]) {
      setProductErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateProductForm = () => {
    const errors = {};

    if (!productForm.title.trim()) {
      errors.title = "عنوان محصول الزامی است";
    }

    if (!productForm.img.trim() && !productImageFile) {
      errors.img = "انتخاب تصویر الزامی است";
    }

    if (productForm.price === "" || Number(productForm.price) < 0) {
      errors.price = "قیمت محصول معتبر نیست";
    }

    if (productForm.count === "" || Number(productForm.count) < 0) {
      errors.count = "موجودی محصول معتبر نیست";
    }

    if (!productForm.category.trim()) {
      errors.category = "دسته‌بندی الزامی است";
    }

    if (!productForm.description.trim()) {
      errors.description = "توضیحات محصول الزامی است";
    }

    setProductErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const handleSaveProduct = async () => {
    if (!canManageProducts) {
      showMessage("⚠️ شما دسترسی مدیریت محصولات ندارید");
      return;
    }

    if (!validateProductForm()) return;

    let imageUrl = productForm.img;
    const productData = {
      title: productForm.title,
      img: imageUrl,
      price: productForm.price,
      count: productForm.count,
      category: productForm.category,
      description: productForm.description,
      isActive: productForm.isActive,
    };

    try {
      if (productImageFile) {
        setProductImageUploading(true);
        const compressed = await compressProductImage(productImageFile);
        const uploaded = await uploadProductImage(compressed);
        imageUrl = uploaded.url;
        productData.img = imageUrl;
      }
      if (editingProductId) {
        await updateProduct(editingProductId, productData);
        showMessage("✅ محصول با موفقیت ویرایش شد");
      } else {
        await createProduct(productData);
        showMessage("✅ محصول جدید با موفقیت اضافه شد");
      }

      resetProductForm();
      await loadProducts();
    } catch (error) {
      console.error("Save product error:", error);
      setProductErrors({
        general: error.message || "خطا در ذخیره محصول",
      });
    } finally {
      setProductImageUploading(false);
    }
  };

  const handleToggleProductStatus = async (product) => {
    if (!canManageProducts) {
      showMessage("⚠️ شما دسترسی مدیریت محصولات ندارید");
      return;
    }

    try {
      if (product.isActive === false) {
        await activateProduct(product.id);
        showMessage("✅ محصول فعال شد");
      } else {
        await deactivateProduct(product.id);
        showMessage("✅ محصول غیرفعال شد");
      }

      await loadProducts();
    } catch (error) {
      console.error("Toggle product error:", error);
      showMessage("❌ خطا در تغییر وضعیت محصول");
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!canManageProducts) {
      showMessage("⚠️ شما دسترسی حذف محصول ندارید");
      return;
    }

    const confirmed = window.confirm(
      "آیا از حذف این محصول مطمئن هستید؟ اگر این محصول قبلاً در سفارش‌ها استفاده شده باشد، بهتر است به جای حذف، آن را غیرفعال کنید."
    );

    if (!confirmed) return;

    try {
      await deleteProduct(productId);
      showMessage("✅ محصول حذف شد");
      await loadProducts();
    } catch (error) {
      console.error("Delete product error:", error);
      showMessage("❌ خطا در حذف محصول");
    }
  };

  const handleLogout = () => {
    logout();
    navigateTo("shop");
  };

  const renderDashboard = () => {
    return (
      <>
        <div className="admin-stats">
          <div className="stat-card">
            <div className="stat-icon">👥</div>
            <div className="stat-info">
              <h3>کل کاربران</h3>
              <p>{stats.usersCount}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">🛒</div>
            <div className="stat-info">
              <h3>مشتری‌ها</h3>
              <p>{stats.customersCount}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">📦</div>
            <div className="stat-info">
              <h3>کل سفارش‌ها</h3>
              <p>{stats.ordersCount}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">🛍️</div>
            <div className="stat-info">
              <h3>محصولات فعال</h3>
              <p>{stats.activeProductsCount}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">✉️</div>
            <div className="stat-info">
              <h3>پیام‌های جدید</h3>
              <p>{stats.newMessagesCount}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">🕓</div>
            <div className="stat-info">
              <h3>در انتظار پرداخت</h3>
              <p>{stats.pendingPaymentsCount}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-info">
              <h3>پرداخت‌شده</h3>
              <p>{stats.paidOrdersCount}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">💰</div>
            <div className="stat-info">
              <h3>مجموع فروش</h3>
              <p>{formatPrice(stats.totalSales)}</p>
            </div>
          </div>
        </div>

        <div className="staff-access-info">
          <div className="info-box">
            <h3>🧭 وضعیت پنل فروشگاه</h3>
            <ul>
              <li>✓ سفارش‌ها از داخل حساب کاربران خوانده می‌شوند.</li>
              <li>✓ پرداخت‌ها بر اساس وضعیت سفارش‌ها نمایش داده می‌شوند.</li>
              <li>✓ ادمین و کارمند می‌توانند وضعیت پرداخت و ارسال سفارش را تغییر دهند.</li>
              <li>✓ مدیریت محصولات برای ادمین و کارمند فعال است.</li>
              <li>✓ پیام‌های تماس با ما در تب پیام‌ها نمایش داده می‌شوند.</li>
              <li>✓ ادمین اصلی می‌تواند اطلاعات کارمندها را ویرایش کند.</li>
            </ul>
          </div>
        </div>
      </>
    );
  };

  const renderCreateStaffForm = () => {
    if (!isAdmin || activeTab !== "staff") return null;

    return (
      <div className="create-staff-section">
        {!showStaffForm ? (
          <button
            className="create-staff-btn"
            type="button"
            onClick={() => {
              setShowStaffForm(true);
              resetEditStaffForm();
            }}
          >
            ➕ ایجاد کارمند جدید
          </button>
        ) : (
          <div className="staff-form-card">
            <h3>➕ اطلاعات کارمند جدید</h3>

            {formErrors.general && (
              <div className="error-message" style={{ marginBottom: "15px" }}>
                {formErrors.general}
              </div>
            )}

            <div className="staff-form">
              <div className="form-group">
                <label>نام کاربری *</label>
                <input
                  type="text"
                  name="username"
                  value={newStaff.username}
                  onChange={handleStaffFormChange}
                  placeholder="مثال: reza_karimi"
                  className={formErrors.username ? "error" : ""}
                />
                {formErrors.username && (
                  <span className="error-text">{formErrors.username}</span>
                )}
              </div>

              <div className="form-group">
                <label>شماره موبایل *</label>
                <input
                  type="tel"
                  name="phone"
                  value={newStaff.phone}
                  onChange={handleStaffFormChange}
                  placeholder="09123456789"
                  dir="ltr"
                  className={formErrors.phone ? "error" : ""}
                />
                {formErrors.phone && (
                  <span className="error-text">{formErrors.phone}</span>
                )}
              </div>

              <div className="form-group">
                <label>ایمیل *</label>
                <input
                  type="email"
                  name="email"
                  value={newStaff.email}
                  onChange={handleStaffFormChange}
                  placeholder="example@email.com"
                  dir="ltr"
                  className={formErrors.email ? "error" : ""}
                />
                {formErrors.email && (
                  <span className="error-text">{formErrors.email}</span>
                )}
              </div>

              <div className="form-group">
                <label>رمز عبور *</label>
                <input
                  type="password"
                  name="password"
                  value={newStaff.password}
                  onChange={handleStaffFormChange}
                  className={formErrors.password ? "error" : ""}
                />
                {formErrors.password && (
                  <span className="error-text">{formErrors.password}</span>
                )}
              </div>

              <div className="form-group">
                <label>تکرار رمز عبور *</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={newStaff.confirmPassword}
                  onChange={handleStaffFormChange}
                  className={formErrors.confirmPassword ? "error" : ""}
                />
                {formErrors.confirmPassword && (
                  <span className="error-text">
                    {formErrors.confirmPassword}
                  </span>
                )}
              </div>

              <div className="staff-form-buttons">
                <button
                  type="button"
                  onClick={handleCreateStaff}
                  className="btn-submit"
                >
                  ✅ ایجاد
                </button>

                <button type="button" onClick={resetStaffForm} className="btn-cancel">
                  ❌ انصراف
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderEditStaffForm = () => {
    if (!isAdmin || activeTab !== "staff" || !editingStaffId) return null;

    return (
      <div className="staff-form-card">
        <h3>✏️ ویرایش اطلاعات کارمند</h3>

        {editStaffErrors.general && (
          <div className="error-message" style={{ marginBottom: "15px" }}>
            {editStaffErrors.general}
          </div>
        )}

        <div className="staff-form">
          <div className="form-group">
            <label>نام کاربری *</label>
            <input
              type="text"
              name="username"
              value={editStaff.username}
              onChange={handleEditStaffChange}
              className={editStaffErrors.username ? "error" : ""}
            />
            {editStaffErrors.username && (
              <span className="error-text">{editStaffErrors.username}</span>
            )}
          </div>

          <div className="form-group">
            <label>شماره موبایل *</label>
            <input
              type="tel"
              name="phone"
              value={editStaff.phone}
              onChange={handleEditStaffChange}
              dir="ltr"
              className={editStaffErrors.phone ? "error" : ""}
            />
            {editStaffErrors.phone && (
              <span className="error-text">{editStaffErrors.phone}</span>
            )}
          </div>

          <div className="form-group">
            <label>ایمیل *</label>
            <input
              type="email"
              name="email"
              value={editStaff.email}
              onChange={handleEditStaffChange}
              dir="ltr"
              className={editStaffErrors.email ? "error" : ""}
            />
            {editStaffErrors.email && (
              <span className="error-text">{editStaffErrors.email}</span>
            )}
          </div>

          <div className="staff-form-buttons">
            <button type="button" onClick={handleUpdateStaff} className="btn-submit">
              ✅ ذخیره تغییرات
            </button>

            <button type="button" onClick={resetEditStaffForm} className="btn-cancel">
              ❌ انصراف
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderUsersTable = () => {
    return (
      <>
        {renderCreateStaffForm()}
        {renderEditStaffForm()}

        <div className="admin-search">
          <input
            type="text"
            placeholder="🔍 جستجوی کاربر بر اساس نام، شماره موبایل یا ایمیل..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>شناسه</th>
                <th>نام کاربری</th>
                <th>نام کامل</th>
                <th>شماره موبایل</th>
                <th>ایمیل</th>
                <th>نقش</th>
                <th>سفارش‌ها</th>
                <th>آدرس‌ها</th>
                <th>تاریخ عضویت</th>
                {isAdmin && <th>عملیات</th>}
              </tr>
            </thead>

            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td
                    colSpan={isAdmin ? "10" : "9"}
                    style={{ textAlign: "center" }}
                  >
                    هیچ کاربری یافت نشد
                  </td>
                </tr>
              ) : (
                filteredUsers.map((userItem) => {
                  const ordersCount = Array.isArray(userItem.orders)
                    ? userItem.orders.length
                    : 0;

                  const addressesCount = Array.isArray(userItem.addresses)
                    ? userItem.addresses.length
                    : 0;

                  return (
                    <tr key={userItem.id}>
                      <td>{userItem.id}</td>
                      <td>{userItem.username || "-"}</td>
                      <td>{getUserFullName(userItem)}</td>
                      <td dir="ltr">{userItem.phone || "-"}</td>
                      <td dir="ltr">{userItem.email || "-"}</td>
                      <td>
                        <span className={`role-badge ${getRoleClass(userItem.role)}`}>
                          {getRoleName(userItem.role)}
                        </span>
                      </td>
                      <td>{ordersCount}</td>
                      <td>{addressesCount}</td>
                      <td>{formatDate(userItem.createdAt)}</td>

                      {isAdmin && (
                        <td>
                          {activeTab === "staff" && userItem.role === "staff" && (
                            <button
                              type="button"
                              onClick={() => openEditStaffForm(userItem)}
                              className="admin-btn admin-btn-secondary"
                              title="ویرایش کارمند"
                              style={{ marginLeft: "6px" }}
                            >
                              ✏️ ویرایش
                            </button>
                          )}

                          {userItem.role !== "super_admin" &&
                          String(userItem.id) !== String(user?.id) ? (
                            <button
                              type="button"
                              onClick={() =>
                                handleDeleteUser(userItem.id, userItem.role)
                              }
                              className="delete-btn"
                              title="حذف کاربر"
                            >
                              🗑️
                            </button>
                          ) : (
                            <span className="protected-badge">
                              🔒 محافظت شده
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  const renderOrdersTable = () => {
    return (
      <>
        <div className="admin-search">
          <input
            type="text"
            placeholder="🔍 جستجوی سفارش بر اساس کد سفارش، نام مشتری، موبایل، ایمیل یا نام محصول..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>کد سفارش</th>
                <th>مشتری</th>
                <th>شماره موبایل</th>
                <th>تعداد کالا</th>
                <th>اقلام سفارش</th>
                <th>روش ارسال</th>
                <th>مبلغ نهایی</th>
                <th>وضعیت سفارش</th>
                <th>وضعیت پرداخت</th>
                <th>تاریخ ثبت</th>
                <th>عملیات</th>
              </tr>
            </thead>

            <tbody>
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan="11" style={{ textAlign: "center" }}>
                    هنوز سفارشی ثبت نشده است
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => (
                  <tr key={`${order.userId}-${order.id}`}>
                    <td dir="ltr">{getOrderCode(order)}</td>
                    <td>{order.userName || "-"}</td>
                    <td dir="ltr">{order.customer?.phone || order.userPhone || "-"}</td>
                    <td>{Array.isArray(order.items) ? order.items.length : 0}</td>

                    <td
                      style={{
                        minWidth: "240px",
                        whiteSpace: "normal",
                        lineHeight: "1.9",
                        textAlign: "right",
                      }}
                    >
                      {Array.isArray(order.items) && order.items.length > 0 ? (
                        order.items.map((item) => (
                          <div key={`${order.id}-${item.id}`}>
                            {item.title} × {item.quantity}
                            {item.price ? ` — ${formatPrice(item.price)} تومان` : ""}
                          </div>
                        ))
                      ) : (
                        "-"
                      )}
                    </td>

                    <td>{order.shippingTitle || "-"}</td>
                    <td>{formatPrice(order.finalTotal || order.total)} تومان</td>

                    <td>
                      <div style={{ minWidth: "150px" }}>
                        <div style={{ marginBottom: "8px" }}>
                          {getOrderStatusLabel(order.orderStatus)}
                        </div>

                        <select
                          value={order.orderStatus || "waiting_for_payment"}
                          onChange={(e) =>
                            handleChangeOrderStatus(order, e.target.value)
                          }
                          disabled={!canManageOrders}
                          style={{
                            width: "100%",
                            padding: "7px",
                            borderRadius: "10px",
                            border: "1px solid rgba(122, 23, 54, 0.22)",
                            fontFamily: "inherit",
                          }}
                        >
                          <option value="waiting_for_payment">در انتظار پرداخت</option>
                          <option value="processing">در حال آماده‌سازی</option>
                          <option value="shipped">ارسال شد</option>
                          <option value="delivered">تحویل شد</option>
                          <option value="cancelled">لغو شد</option>
                        </select>
                      </div>
                    </td>

                    <td>
                      <div style={{ minWidth: "140px" }}>
                        <div style={{ marginBottom: "8px" }}>
                          {getPaymentStatusLabel(order.paymentStatus)}
                        </div>

                        <select
                          value={order.paymentStatus || "pending"}
                          onChange={(e) =>
                            handleChangePaymentStatus(order, e.target.value)
                          }
                          disabled={!canManageOrders}
                          style={{
                            width: "100%",
                            padding: "7px",
                            borderRadius: "10px",
                            border: "1px solid rgba(122, 23, 54, 0.22)",
                            fontFamily: "inherit",
                          }}
                        >
                          <option value="pending">در انتظار پرداخت</option>
                          <option value="paid">پرداخت شد</option>
                          <option value="failed">پرداخت رد شد</option>
                        </select>
                      </div>
                    </td>

                    <td>{formatDate(order.createdAt)}</td>

                    <td>
                      <button
                        type="button"
                        className="admin-btn admin-btn-secondary"
                        onClick={() => handleQuickMarkPaid(order)}
                        style={{ marginLeft: "6px", marginBottom: "6px" }}
                        disabled={order.paymentStatus === "paid"}
                      >
                        پرداخت شد
                      </button>

                      <button
                        type="button"
                        className="admin-btn admin-btn-secondary"
                        onClick={() => handleQuickMarkShipped(order)}
                        disabled={order.orderStatus === "shipped"}
                      >
                        ارسال شد
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  const renderPaymentsTable = () => {
    return (
      <>
        <div className="admin-search">
          <input
            type="text"
            placeholder="🔍 جستجوی پرداخت بر اساس کد سفارش، نام مشتری یا موبایل..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>کد سفارش</th>
                <th>مشتری</th>
                <th>شماره موبایل</th>
                <th>روش پرداخت</th>
                <th>مبلغ</th>
                <th>وضعیت پرداخت</th>
                <th>وضعیت سفارش</th>
                <th>تاریخ</th>
                <th>عملیات</th>
              </tr>
            </thead>

            <tbody>
              {filteredPayments.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: "center" }}>
                    هنوز پرداختی ثبت نشده است
                  </td>
                </tr>
              ) : (
                filteredPayments.map((payment) => (
                  <tr key={payment.id}>
                    <td dir="ltr">{payment.orderCode || payment.orderId}</td>
                    <td>{payment.customerName || "-"}</td>
                    <td dir="ltr">{payment.phone || "-"}</td>
                    <td>{getPaymentMethodLabel(payment.paymentMethod)}</td>
                    <td>{formatPrice(payment.amount)} تومان</td>

                    <td>
                      <select
                        value={payment.paymentStatus || "pending"}
                        onChange={(e) =>
                          handleChangePaymentStatus(payment.order, e.target.value)
                        }
                        disabled={!canManageOrders}
                        style={{
                          width: "150px",
                          padding: "7px",
                          borderRadius: "10px",
                          border: "1px solid rgba(122, 23, 54, 0.22)",
                          fontFamily: "inherit",
                        }}
                      >
                        <option value="pending">در انتظار پرداخت</option>
                        <option value="paid">پرداخت شد</option>
                        <option value="failed">پرداخت رد شد</option>
                      </select>
                    </td>

                    <td>{getOrderStatusLabel(payment.order?.orderStatus)}</td>
                    <td>{formatDate(payment.createdAt)}</td>

                    <td>
                      <button
                        type="button"
                        className="admin-btn admin-btn-secondary"
                        onClick={() => handleQuickMarkPaid(payment.order)}
                        disabled={payment.paymentStatus === "paid"}
                      >
                        پرداخت شد
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  const renderContactMessagesTable = () => {
    return (
      <>
        <div className="admin-search">
          <input
            type="text"
            placeholder="🔍 جستجوی پیام بر اساس نام، شماره، ایمیل، موضوع یا متن پیام..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>نام</th>
                <th>شماره تماس</th>
                <th>ایمیل</th>
                <th>موضوع</th>
                <th>متن پیام</th>
                <th>وضعیت</th>
                <th>تاریخ</th>
                <th>عملیات</th>
              </tr>
            </thead>

            <tbody>
              {contactMessagesLoading ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: "center" }}>
                    در حال دریافت پیام‌ها...
                  </td>
                </tr>
              ) : filteredContactMessages.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: "center" }}>
                    هنوز پیامی ثبت نشده است
                  </td>
                </tr>
              ) : (
                filteredContactMessages.map((item) => (
                  <tr key={item.id}>
                    <td>{item.name || "-"}</td>
                    <td dir="ltr">{item.phone || "-"}</td>
                    <td dir="ltr">{item.email || "-"}</td>
                    <td>{item.subject || "پیام تماس با ما"}</td>
                    <td
                      style={{
                        maxWidth: "340px",
                        whiteSpace: "normal",
                        lineHeight: "1.9",
                        textAlign: "right",
                      }}
                    >
                      {item.message || "-"}
                    </td>
                    <td>{getMessageStatusLabel(item.status)}</td>
                    <td>{formatDate(item.createdAt)}</td>
                    <td>
                      <button
                        type="button"
                        className="admin-btn admin-btn-secondary"
                        onClick={() => handleChangeMessageStatus(item.id, "seen")}
                        style={{ marginLeft: "6px" }}
                      >
                        دیده شد
                      </button>

                      <button
                        type="button"
                        className="admin-btn admin-btn-secondary"
                        onClick={() => handleChangeMessageStatus(item.id, "followed")}
                        style={{ marginLeft: "6px" }}
                      >
                        پیگیری شد
                      </button>

                      <button
                        type="button"
                        className="admin-btn admin-btn-secondary"
                        onClick={() => handleChangeMessageStatus(item.id, "closed")}
                      >
                        بستن
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  const renderProductForm = () => {
    if (!showProductForm) return null;

    return (
      <div className="staff-form-card">
        <h3>{editingProductId ? "✏️ ویرایش محصول" : "➕ افزودن محصول جدید"}</h3>

        {productErrors.general && (
          <div className="error-message" style={{ marginBottom: "15px" }}>
            {productErrors.general}
          </div>
        )}

        <div className="staff-form">
          <div className="form-group">
            <label>عنوان محصول *</label>
            <input
              type="text"
              name="title"
              value={productForm.title}
              onChange={handleProductFormChange}
              placeholder="مثال: لیوان سفالی"
              className={productErrors.title ? "error" : ""}
            />
            {productErrors.title && (
              <span className="error-text">{productErrors.title}</span>
            )}
          </div>

          <div className="form-group">
            <label>تصویر محصول *</label>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(event) => {
                setProductImageFile(event.target.files?.[0] || null);
                setProductErrors((prev) => ({ ...prev, img: "" }));
              }}
              className={productErrors.img ? "error" : ""}
            />
            {productForm.img && !productImageFile && <span className="form-hint">تصویر فعلی حفظ می‌شود؛ برای تعویض، فایل جدید انتخاب کنید.</span>}
            {productErrors.img && (
              <span className="error-text">{productErrors.img}</span>
            )}
          </div>

          <div className="form-group">
            <label>قیمت *</label>
            <input
              type="number"
              name="price"
              value={productForm.price}
              onChange={handleProductFormChange}
              min="0"
              className={productErrors.price ? "error" : ""}
            />
            {productErrors.price && (
              <span className="error-text">{productErrors.price}</span>
            )}
          </div>

          <div className="form-group">
            <label>موجودی *</label>
            <input
              type="number"
              name="count"
              value={productForm.count}
              onChange={handleProductFormChange}
              min="0"
              className={productErrors.count ? "error" : ""}
            />
            {productErrors.count && (
              <span className="error-text">{productErrors.count}</span>
            )}
          </div>

          <div className="form-group">
            <label>دسته‌بندی *</label>
            <input
              type="text"
              name="category"
              value={productForm.category}
              onChange={handleProductFormChange}
              placeholder="مثال: سفال"
              className={productErrors.category ? "error" : ""}
            />
            {productErrors.category && (
              <span className="error-text">{productErrors.category}</span>
            )}
          </div>

          <div className="form-group">
            <label>وضعیت نمایش</label>
            <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <input
                type="checkbox"
                name="isActive"
                checked={productForm.isActive}
                onChange={handleProductFormChange}
                style={{ width: "auto" }}
              />
              محصول فعال باشد
            </label>
          </div>

          <div className="form-group" style={{ gridColumn: "1 / -1" }}>
            <label>توضیحات محصول *</label>
            <textarea
              name="description"
              value={productForm.description}
              onChange={handleProductFormChange}
              placeholder="توضیحات محصول را وارد کنید..."
              className={productErrors.description ? "error" : ""}
              rows="4"
              style={{
                width: "100%",
                padding: "12px 13px",
                borderRadius: "14px",
                border: "1px solid rgba(122, 23, 54, 0.14)",
                fontFamily: "inherit",
                resize: "vertical",
                lineHeight: "1.9",
              }}
            />
            {productErrors.description && (
              <span className="error-text">{productErrors.description}</span>
            )}
          </div>

          <div className="staff-form-buttons">
            <button type="button" onClick={handleSaveProduct} className="btn-submit" disabled={productImageUploading}>
              ✅ {productImageUploading ? "در حال بارگذاری تصویر..." : editingProductId ? "ذخیره تغییرات" : "افزودن محصول"}
            </button>

            <button type="button" onClick={resetProductForm} className="btn-cancel">
              ❌ انصراف
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderProductsTable = () => {
    return (
      <>
        <div className="create-staff-section">
          <button
            className="create-staff-btn"
            type="button"
            onClick={openNewProductForm}
          >
            ➕ افزودن محصول جدید
          </button>
        </div>

        {renderProductForm()}

        <div className="admin-search">
          <input
            type="text"
            placeholder="🔍 جستجوی محصول بر اساس عنوان، دسته‌بندی، توضیحات یا نام تصویر..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>تصویر</th>
                <th>عنوان</th>
                <th>دسته‌بندی</th>
                <th>قیمت</th>
                <th>موجودی</th>
                <th>وضعیت</th>
                <th>توضیحات</th>
                <th>عملیات</th>
              </tr>
            </thead>

            <tbody>
              {productsLoading ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: "center" }}>
                    در حال دریافت محصولات...
                  </td>
                </tr>
              ) : filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: "center" }}>
                    محصولی یافت نشد
                  </td>
                </tr>
              ) : (
                filteredProducts.map((product) => (
                  <tr key={product.id}>
                    <td>
                      <img
                        src={getImageSrc(product.img)}
                        alt={product.title}
                        style={{
                          width: "54px",
                          height: "54px",
                          objectFit: "contain",
                          borderRadius: "12px",
                          background: "#f7f5f4",
                        }}
                      />
                    </td>
                    <td>{product.title}</td>
                    <td>{product.category || "-"}</td>
                    <td>{formatPrice(product.price)} تومان</td>
                    <td>{product.count}</td>
                    <td>
                      <span
                        className={
                          product.isActive === false
                            ? "protected-badge"
                            : "role-badge role-customer"
                        }
                      >
                        {product.isActive === false ? "غیرفعال" : "فعال"}
                      </span>
                    </td>
                    <td>
                      {product.description?.length > 45
                        ? `${product.description.slice(0, 45)}...`
                        : product.description || "-"}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="admin-btn admin-btn-secondary"
                        onClick={() => openEditProductForm(product)}
                        style={{ marginLeft: "6px" }}
                      >
                        ✏️ ویرایش
                      </button>

                      <button
                        type="button"
                        className="admin-btn admin-btn-secondary"
                        onClick={() => handleToggleProductStatus(product)}
                        style={{ marginLeft: "6px" }}
                      >
                        {product.isActive === false ? "✅ فعال" : "⛔ غیرفعال"}
                      </button>

                      <button
                        type="button"
                        className="delete-btn"
                        onClick={() => handleDeleteProduct(product.id)}
                      >
                        🗑️ حذف
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {isStaff && !isAdmin && (
          <div className="staff-access-info">
            <div className="info-box">
              <h3>🛍️ دسترسی مدیریت محصول</h3>
              <ul>
                <li>✓ کارمند می‌تواند محصول اضافه کند.</li>
                <li>✓ کارمند می‌تواند محصول را ویرایش کند.</li>
                <li>✓ کارمند می‌تواند موجودی، قیمت، توضیحات و عکس محصول را تغییر دهد.</li>
                <li>✓ کارمند می‌تواند محصول را فعال یا غیرفعال کند.</li>
              </ul>
            </div>
          </div>
        )}
      </>
    );
  };

  const renderRecoveryRequests = () => (
    <div className="users-table-container">
      <table className="users-table">
        <thead><tr><th>کد پیگیری</th><th>کاربر</th><th>شماره</th><th>زمان</th><th>وضعیت</th><th>عملیات</th></tr></thead>
        <tbody>
          {recoveryRequests.length === 0 ? <tr><td colSpan="6" style={{textAlign:"center"}}>درخواست بازیابی جدیدی وجود ندارد</td></tr> : recoveryRequests.map((item) => (
            <tr key={item.id}>
              <td dir="ltr">{item.request_code}</td><td>{item.username || "-"}</td><td dir="ltr">{item.phone}</td>
              <td>{formatDate(item.created_at)}</td><td>{item.status === "new" ? "جدید" : item.status === "approved" ? "تأییدشده" : item.status}</td>
              <td>
                {item.status === "new" && (
                  <button
                    type="button"
                    className="action-btn edit"
                    onClick={async () => {
                      try {
                        const result = await approveRecoveryRequest(item.id);

                        setRecoveryResetLink({
                          id: item.id,
                          url: result.resetUrl,
                          phone: item.phone,
                          username: item.username,
                        });

                        showMessage("✅ لینک بازیابی ۲۴ ساعته ساخته شد");
                        await loadRecoveryRequests();
                      } catch (error) {
                        showMessage("❌ " + error.message);
                      }
                    }}
                  >
                    تأیید و دریافت لینک
                  </button>
                )}

                {recoveryResetLink?.id === item.id && (
                  <div style={{ marginTop: "8px", minWidth: "280px" }}>
                    <input
                      id={`recovery-link-${item.id}`}
                      type="text"
                      dir="ltr"
                      readOnly
                      value={recoveryResetLink.url}
                      onFocus={(event) => event.target.select()}
                      style={{ width: "100%", padding: "7px" }}
                    />

                    <div
                      style={{
                        display: "flex",
                        gap: "6px",
                        marginTop: "6px",
                        flexWrap: "wrap",
                      }}
                    >
                      <button
                        type="button"
                        className="action-btn edit"
                        onClick={async () => {
                          const input = document.getElementById(
                            `recovery-link-${item.id}`
                          );

                          input?.select();

                          try {
                            await navigator.clipboard.writeText(recoveryResetLink.url);
                          } catch {
                            document.execCommand("copy");
                          }

                          showMessage("✅ لینک کپی شد");
                        }}
                      >
                        کپی لینک
                      </button>

                      <a
                        className="action-btn edit"
                        href={`https://wa.me/${String(item.phone || "")
                          .replace(/\D/g, "")
                          .replace(/^0/, "98")}?text=${encodeURIComponent(
                          `سلام ${item.username || ""}، لینک امن تغییر رمز نقش خیال (معتبر تا ۲۴ ساعت):\n${recoveryResetLink.url}`
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: "none" }}
                      >
                        ارسال در واتساپ
                      </a>

                      <button
                        type="button"
                        className="action-btn"
                        onClick={() => setRecoveryResetLink(null)}
                      >
                        بستن
                      </button>
                    </div>

                    <small style={{ display: "block", marginTop: "6px" }}>
                      این لینک ۲۴ ساعت اعتبار دارد و فقط برای همان کاربر ارسال شود.
                    </small>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <div className="admin-header-content">
          <h1>
            {isAdmin ? "👑 پنل مدیریت نقشِ خیال" : "📋 پنل کارمندان نقشِ خیال"}
          </h1>

          <div className="admin-actions">
            <span className="admin-welcome">
              خوش آمدی {user?.username || user?.firstName || "کاربر"} (
              {getRoleName(user?.role)})
            </span>

            <button
              type="button"
              onClick={() => navigateTo("shop")}
              className="admin-btn admin-btn-secondary"
            >
              🏪 بازگشت به صفحه اصلی
            </button>

            <button type="button" onClick={handleLogout} className="admin-btn-logout">
              🚪 خروج
            </button>
          </div>
        </div>
      </div>

      <div className="admin-container">
        {message && <div className="admin-message success">{message}</div>}

        <div className="admin-tabs">
          <button
            type="button"
            className={`tab-btn ${activeTab === "dashboard" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("dashboard");
              setSearchTerm("");
              resetEditStaffForm();
              resetProductForm();
            }}
          >
            📊 داشبورد
          </button>

          <button
            type="button"
            className={`tab-btn ${activeTab === "customers" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("customers");
              setSearchTerm("");
              resetEditStaffForm();
              resetProductForm();
            }}
          >
            🛒 مشتری‌ها
          </button>

          {isAdmin && (
            <button
              type="button"
              className={`tab-btn ${activeTab === "staff" ? "active" : ""}`}
              onClick={() => {
                setActiveTab("staff");
                setSearchTerm("");
                resetProductForm();
              }}
            >
              👔 کارمندان
            </button>
          )}

          {isAdmin && (
            <button
              type="button"
              className={`tab-btn ${activeTab === "all-users" ? "active" : ""}`}
              onClick={() => {
                setActiveTab("all-users");
                setSearchTerm("");
                resetEditStaffForm();
                resetProductForm();
              }}
            >
              👥 همه کاربران
            </button>
          )}

          <button
            type="button"
            className={`tab-btn ${activeTab === "orders" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("orders");
              setSearchTerm("");
              resetEditStaffForm();
              resetProductForm();
              loadOrders();
            }}
          >
            📦 سفارش‌ها
          </button>

          <button
            type="button"
            className={`tab-btn ${activeTab === "payments" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("payments");
              setSearchTerm("");
              resetEditStaffForm();
              resetProductForm();
              loadOrders();
            }}
          >
            💳 پرداخت‌ها
          </button>

          <button
            type="button"
            className={`tab-btn ${activeTab === "recovery" ? "active" : ""}`}
            onClick={() => { setActiveTab("recovery"); setSearchTerm(""); resetEditStaffForm(); resetProductForm(); loadRecoveryRequests(); }}
          >
            🔐 بازیابی رمز {recoveryRequests.filter((item) => item.status === "new").length > 0 ? `(${recoveryRequests.filter((item) => item.status === "new").length})` : ""}
          </button>

          <button
            type="button"
            className={`tab-btn ${activeTab === "messages" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("messages");
              setSearchTerm("");
              resetEditStaffForm();
              resetProductForm();
              loadContactMessages();
            }}
          >
            ✉️ پیام‌ها
          </button>

          <button
            type="button"
            className={`tab-btn ${activeTab === "products" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("products");
              setSearchTerm("");
              resetEditStaffForm();
              resetProductForm();
              loadProducts();
            }}
          >
            🛍️ محصولات
          </button>
        </div>

        {activeTab === "dashboard" && renderDashboard()}

        {(activeTab === "customers" ||
          activeTab === "staff" ||
          activeTab === "all-users") &&
          renderUsersTable()}

        {activeTab === "orders" && renderOrdersTable()}

        {activeTab === "payments" && renderPaymentsTable()}

        {activeTab === "messages" && renderContactMessagesTable()}

        {activeTab === "products" && renderProductsTable()}

        {activeTab === "recovery" && renderRecoveryRequests()}

        {isStaff && !isAdmin && activeTab !== "products" && (
          <div className="staff-access-info">
            <div className="info-box">
              <h3>🔑 دسترسی‌های کارمند</h3>
              <ul>
                <li>✓ مشاهده مشتری‌ها</li>
                <li>✓ مشاهده سفارش‌ها و پرداخت‌ها</li>
                <li>✓ تغییر وضعیت پرداخت و ارسال سفارش‌ها</li>
                <li>✓ مشاهده و پیگیری پیام‌های تماس با ما</li>
                <li>✓ مدیریت کامل محصولات</li>
                <li>× مدیریت کاربران و کارمندان فقط برای ادمین اصلی است</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
